import { useRef, useState } from "react";
import {
  CircleCheck,
  ExternalLink,
  Flag,
  LoaderCircle,
  Map as MapIcon,
  Paperclip,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import {
  FLOW_TYPES,
  resolveFlowType,
  type MindNode,
  type NodeAttachment,
  type NodeComment,
  type NodeStatus,
  type SpaceRef,
} from "@etus-mindmap/shared/api";
import { useDeleteFile, useFileDownloader, useMapsInSpace, useUploadFile } from "../api/hooks";
import { FlowTypePreview } from "../routes/flow-shapes";
import { Drawer } from "./Drawer";

// Painel de conteúdo rico do nó (Fase 3): notas, link, status, cor do ramo,
// comentários e anexos. Toda edição passa por onPatch → mutate do editor
// (autosave + undo). Padrão ETUS: edição em drawer lateral.

export const STATUS_OPTIONS: Array<{ value: NodeStatus; label: string }> = [
  { value: "priority", label: "Prioridade" },
  { value: "doing", label: "Em andamento" },
  { value: "done", label: "Concluído" },
  { value: "flag", label: "Sinalizado" },
];

export function StatusIcon({ status, className }: { status: NodeStatus; className?: string }) {
  const cls = className ?? "h-3.5 w-3.5";
  switch (status) {
    case "priority":
      return <TriangleAlert className={`${cls} text-amber-600 dark:text-amber-400`} aria-label="Prioridade" />;
    case "doing":
      return <LoaderCircle className={`${cls} text-sky-600 dark:text-sky-400`} aria-label="Em andamento" />;
    case "done":
      return <CircleCheck className={`${cls} text-primary dark:text-primary-light`} aria-label="Concluído" />;
    case "flag":
      return <Flag className={`${cls} text-red-600 dark:text-red-400`} aria-label="Sinalizado" />;
  }
}

// Paleta de cores de ramo (conteúdo do usuário, não tema da UI). Verde ETUS
// primeiro; tons que funcionam sobre fundo claro e escuro.
export const BRANCH_PALETTE = [
  "#066E3E",
  "#ef4444",
  "#f59e0b",
  "#0ea5e9",
  "#8b5cf6",
  "#ec4899",
  "#64748b",
] as const;

const label = "mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400";
const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500";

export function NodeDetailsPanel({
  mapUuid,
  mapSpace,
  node,
  authorEmail,
  showShape = false,
  onPatch,
  onClose,
  onOpenLinkedMap,
}: {
  mapUuid: string | null;
  mapSpace?: SpaceRef | null;
  node: MindNode;
  authorEmail: string;
  showShape?: boolean;
  onPatch: (id: string, patch: Partial<MindNode>) => void;
  onClose: () => void;
  onOpenLinkedMap?: (uuid: string) => void;
}) {
  return (
    <Drawer open title={node.label} onClose={onClose}>
      {/* key força remount ao trocar de nó, zerando drafts locais */}
      <PanelBody
        key={node.id}
        mapUuid={mapUuid}
        mapSpace={mapSpace ?? null}
        node={node}
        authorEmail={authorEmail}
        showShape={showShape}
        onPatch={onPatch}
        onOpenLinkedMap={onOpenLinkedMap}
      />
    </Drawer>
  );
}

function PanelBody({
  mapUuid,
  mapSpace,
  node,
  authorEmail,
  showShape,
  onPatch,
  onOpenLinkedMap,
}: {
  mapUuid: string | null;
  mapSpace: SpaceRef | null;
  node: MindNode;
  authorEmail: string;
  showShape: boolean;
  onPatch: (id: string, patch: Partial<MindNode>) => void;
  onOpenLinkedMap?: (uuid: string) => void;
}) {
  const [notes, setNotes] = useState(node.notes ?? "");
  const [link, setLink] = useState(node.link ?? "");
  const [commentDraft, setCommentDraft] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  // Anexos dependem de endpoints autenticados (R2). No link público (mapUuid
  // null) a seção some, mas notas/link/comentários seguem no doc.
  const upload = useUploadFile(mapUuid ?? "");
  const deleteFile = useDeleteFile(mapUuid ?? "");
  const download = useFileDownloader(mapUuid ?? "");
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const commitNotes = () => {
    const v = notes.trim();
    if ((node.notes ?? "") !== v) onPatch(node.id, { notes: v || undefined });
  };
  const commitLink = () => {
    const v = link.trim();
    if ((node.link ?? "") !== v) onPatch(node.id, { link: v || undefined });
  };

  const addComment = () => {
    const text = commentDraft.trim();
    if (!text) return;
    const comment: NodeComment = { id: crypto.randomUUID(), author: authorEmail, text, createdAt: Date.now() };
    onPatch(node.id, { comments: [...(node.comments ?? []), comment] });
    setCommentDraft("");
  };

  const removeComment = (id: string) => {
    onPatch(node.id, { comments: (node.comments ?? []).filter((c) => c.id !== id) });
  };

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    const att = await upload.mutateAsync(file);
    onPatch(node.id, { attachments: [...(node.attachments ?? []), att] });
    if (fileInput.current) fileInput.current.value = "";
  };

  const removeAttachment = async (att: NodeAttachment) => {
    await deleteFile.mutateAsync(att.id);
    onPatch(node.id, { attachments: (node.attachments ?? []).filter((a) => a.id !== att.id) });
  };

  return (
    <div className="space-y-5">
      <div>
        <span className={label}>Notas</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={commitNotes}
          rows={4}
          placeholder="Descrição, contexto, markdown leve…"
          className={inputCls}
        />
      </div>

      <div>
        <span className={label}>Link externo</span>
        <div className="flex items-center gap-2">
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            onBlur={commitLink}
            placeholder="https://…"
            className={inputCls}
          />
          {node.link && (
            <a
              href={node.link}
              target="_blank"
              rel="noreferrer"
              title="Abrir link"
              aria-label="Abrir link"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
          )}
        </div>
      </div>

      {/* Atalho para outro mapa. Só no modo autenticado (mapUuid presente): a
          listagem de mapas exige auth; no link público a seção some. */}
      {mapUuid && (
        <MapLinkSection
          mapUuid={mapUuid}
          mapSpace={mapSpace}
          node={node}
          onPatch={onPatch}
          onOpenLinkedMap={onOpenLinkedMap}
        />
      )}

      {showShape && (
        <div>
          <span className={label}>Tipo (fluxograma)</span>
          <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
            <FlowTypePreview flowType={resolveFlowType(node)} className="h-4 w-7 shrink-0" />
            {FLOW_TYPES[resolveFlowType(node)].label}
          </div>
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            O tipo é definido ao adicionar o nó e não pode ser trocado.
            {resolveFlowType(node) === "subprocess" && " Use “Atalho para outro mapa” para vincular o subprocesso."}
          </p>
        </div>
      )}

      <div>
        <span className={label}>Status</span>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((opt) => {
            const active = node.status === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={active}
                onClick={() => onPatch(node.id, { status: active ? undefined : opt.value })}
                className={[
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light"
                    : "border-slate-300 text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500",
                ].join(" ")}
              >
                <StatusIcon status={opt.value} className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className={label}>Cor do ramo</span>
        <div className="flex items-center gap-1.5">
          {BRANCH_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              aria-label={`Cor ${c}`}
              aria-pressed={node.color === c}
              onClick={() => onPatch(node.id, { color: node.color === c ? undefined : c })}
              className={[
                "h-6 w-6 rounded-full border-2 transition-transform hover:scale-110",
                node.color === c ? "border-slate-900 dark:border-slate-100" : "border-transparent",
              ].join(" ")}
              style={{ backgroundColor: c }}
            />
          ))}
          {node.color && (
            <button
              type="button"
              title="Remover cor"
              aria-label="Remover cor"
              onClick={() => onPatch(node.id, { color: undefined })}
              className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Descendentes herdam a cor do ramo.</p>
      </div>

      {mapUuid && (
      <div>
        <span className={label}>
          Anexos {upload.isPending && <LoaderCircle className="ml-1 inline h-3 w-3 animate-spin" aria-hidden />}
        </span>
        <ul className="space-y-1">
          {(node.attachments ?? []).map((att) => (
            <li
              key={att.id}
              className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm dark:border-slate-800"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
              <button
                type="button"
                onClick={() => {
                  setDownloadError(null);
                  download(att).catch((e) => setDownloadError((e as Error).message));
                }}
                title={`Baixar ${att.name}`}
                className="min-w-0 flex-1 truncate text-left text-slate-700 hover:text-primary dark:text-slate-300 dark:hover:text-primary-light"
              >
                {att.name}
              </button>
              <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{formatSize(att.size)}</span>
              <button
                type="button"
                title="Excluir anexo"
                aria-label={`Excluir ${att.name}`}
                onClick={() => void removeAttachment(att)}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-200"
        >
          <Upload className="h-3.5 w-3.5" aria-hidden />
          Anexar arquivo (máx. 10 MB)
        </button>
        {upload.isError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            Erro no upload: {(upload.error as Error).message}
          </p>
        )}
        {downloadError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">Erro no download: {downloadError}</p>
        )}
      </div>
      )}

      <div>
        <span className={label}>Comentários</span>
        <ul className="space-y-2">
          {(node.comments ?? []).map((c) => (
            <li key={c.id} className="rounded-md border border-slate-200 px-2.5 py-2 dark:border-slate-800">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">{c.author}</span>
                <span className="flex shrink-0 items-center gap-1">
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    {new Date(c.createdAt).toLocaleString()}
                  </span>
                  <button
                    type="button"
                    title="Excluir comentário"
                    aria-label="Excluir comentário"
                    onClick={() => removeComment(c.id)}
                    className="flex h-5 w-5 items-center justify-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{c.text}</p>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <textarea
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            rows={2}
            placeholder="Escreva um comentário…"
            className={inputCls}
          />
        </div>
        <button
          type="button"
          disabled={commentDraft.trim().length === 0}
          onClick={addComment}
          className="mt-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
        >
          Comentar
        </button>
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Atalho para outro mapa: escolhe um mapa do espaço atual (useMaps) e grava o
// uuid no nó (linkedMapUuid). O nó vira um link de navegação no editor. Fica
// num componente próprio porque useMaps só deve rodar no modo autenticado.
function MapLinkSection({
  mapUuid,
  mapSpace,
  node,
  onPatch,
  onOpenLinkedMap,
}: {
  mapUuid: string;
  mapSpace: SpaceRef | null;
  node: MindNode;
  onPatch: (id: string, patch: Partial<MindNode>) => void;
  onOpenLinkedMap?: (uuid: string) => void;
}) {
  // Lista mapas do espaço DO MAPA (a mesma org/espaço), não do espaço ativo
  // global — abrir um mapa não muda o espaço ativo, então usar o ativo traria
  // a lista errada.
  const maps = useMapsInSpace(mapSpace);
  const linkedUuid = typeof node.linkedMapUuid === "string" ? node.linkedMapUuid : undefined;
  // Opções = mapas do mesmo espaço, exceto o próprio (evita atalho circular).
  const options = (maps.data ?? []).filter((m) => m.uuid !== mapUuid);
  const linked = linkedUuid ? maps.data?.find((m) => m.uuid === linkedUuid) : undefined;

  return (
    <div>
      <span className={label}>Atalho para outro mapa</span>
      {linkedUuid && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 dark:border-slate-800">
          <MapIcon className="h-4 w-4 shrink-0 text-primary dark:text-primary-light" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-300">
            {linked ? linked.title : "Mapa de outro espaço ou removido"}
          </span>
          {onOpenLinkedMap && (
            <button
              type="button"
              onClick={() => onOpenLinkedMap(linkedUuid)}
              title="Abrir mapa vinculado"
              aria-label="Abrir mapa vinculado"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-primary-light"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={() => onPatch(node.id, { linkedMapUuid: undefined })}
            title="Remover atalho"
            aria-label="Remover atalho"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
      <select
        value={linkedUuid ?? ""}
        onChange={(e) => onPatch(node.id, { linkedMapUuid: e.target.value || undefined })}
        aria-label="Vincular a outro mapa"
        className={inputCls}
      >
        <option value="">{linkedUuid ? "Trocar mapa vinculado…" : "Nenhum — escolher um mapa…"}</option>
        {options.map((m) => (
          <option key={m.uuid} value={m.uuid}>
            {m.title}
          </option>
        ))}
      </select>
      {maps.isLoading && <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Carregando mapas…</p>}
      {maps.data && options.length === 0 && (
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Nenhum outro mapa neste espaço.</p>
      )}
    </div>
  );
}
